<template>
  <div class="shift-view-info">
    <shift-main :shift="shift" :associate="associate">
      <template slot="actions">
        <a-button block data-testid="shift-view-manage-note-button" @click="visibleNoteModal = true">
          Manage Note
        </a-button>
        <export-button
          label="Export Shift Time Card"
          type="default"
          :button-style="{ width: '100%' }"
          @send="sendReport"
          @download="downloadReport"
        />
        <a-button
          block
          type="danger"
          :disabled="isActionRestricted"
          data-testid="shift-view-delete-button"
          @click="$emit('delete-shift')"
        >
          Delete Shift
        </a-button>
      </template>
    </shift-main>

    <section>
      <section-divider title="Status" />
      <div class="shift-info-grid">
        <div class="shift-info-field">
          <span>Shift Status</span>
          <a-tag class="m-0" :color="getShiftStatus(shift.status).color">
            {{ getShiftStatus(shift.status).title }}
          </a-tag>
        </div>
        <div v-if="shift.pay_code" class="shift-info-field">
          <span>Pay Code</span>
          <a-tag class="m-0" color="#d47aff" :title="shift.pay_code.description">
            {{ shift.pay_code.name }}
          </a-tag>
        </div>
        <div
          v-if="typeof shift.is_planned === 'boolean' && (shift.pay_code || shift.status === 'cancelled')"
          class="shift-info-field"
        >
          <span>Schedule</span>
          <strong>{{ shift.is_planned ? 'Planned' : 'Unplanned' }}</strong>
        </div>
        <div class="shift-info-field">
          <span>Ready for Pay Status</span>
          <a-tag class="m-0" :color="getReadyForPaymentStatus(shift.internal_payment_info.ready_for_pay_status).color">
            {{ getReadyForPaymentStatus(shift.internal_payment_info.ready_for_pay_status).title }}
          </a-tag>
        </div>
        <div v-if="shift.internal_payment_info.not_ready_to_pay_flags.length" class="shift-info-field">
          <span>Reason</span>
          <div class="flex flex-wrap gap-2">
            <a-tag
              v-for="flag in shift.internal_payment_info.not_ready_to_pay_flags"
              :key="flag"
              class="m-0"
              color="#ef4444"
            >
              {{ getNotReadyForPaymentFlag(flag).title }}
            </a-tag>
          </div>
        </div>
        <div class="shift-info-field">
          <span>Origin</span>
          <a-tag class="m-0" :color="getOrigin(shift.origin).color">
            {{ getOrigin(shift.origin).title }}
          </a-tag>
        </div>
      </div>
    </section>

    <section v-if="isStaff && shift.external_payment_info">
      <section-divider title="Worked Company" />
      <div class="shift-info-grid">
        <div class="shift-info-field">
          <span>Company</span>
          <strong>{{ shift.external_payment_info.project.name }}</strong>
        </div>
        <div class="shift-info-field">
          <span>Payment Type</span>
          <strong>{{ shift.external_payment_info.payment_type }}</strong>
        </div>
        <div v-if="!shift.pay_code && shift.status !== 'deleted'" class="shift-info-field">
          <span>
            {{
              shift.external_payment_info.ready_for_pay_status === 'ready' ? 'Payment Status' : 'Ready for Pay Status'
            }}
          </span>
          <a-tag
            v-if="shift.external_payment_info.ready_for_pay_status === 'ready'"
            class="m-0"
            :color="getPaymentStatus.outgoing.color"
          >
            {{ getPaymentStatus.outgoing.title }}
          </a-tag>
          <a-tag
            v-else
            class="m-0"
            :color="getReadyForPaymentStatus(shift.external_payment_info.ready_for_pay_status).color"
          >
            {{ getReadyForPaymentStatus(shift.external_payment_info.ready_for_pay_status).title }}
          </a-tag>
        </div>
        <div
          v-if="
            shift.external_payment_info.not_ready_to_pay_flags.length &&
            ['not_paid', 'paid'].includes(shift.external_payment_info.payment_status)
          "
          class="shift-info-field"
        >
          <span>Reason</span>
          <div class="flex flex-wrap gap-2">
            <a-tag
              v-for="flag in shift.external_payment_info.not_ready_to_pay_flags"
              :key="flag"
              class="m-0"
              color="#ef4444"
            >
              {{ getNotReadyForPaymentFlag(flag).title }}
            </a-tag>
          </div>
        </div>
        <div class="shift-info-field">
          <span>Bill Rate</span>
          <strong>${{ getRate.outgoing.value / 100 }}</strong>
        </div>
        <div v-if="getRate.outgoing.extra" class="shift-info-field">
          <span>Extra</span>
          <strong>${{ getRate.outgoing.extra / 100 }}</strong>
        </div>
      </div>
    </section>

    <div class="shift-info-grid">
      <section>
        <section-divider title="Digital Signature" />
        <signature v-if="shift.signature" key="digitalSignature" :available-signature="shift.signature"/>
        <span v-else>Signature not provided</span>
        <div v-if="shift.explanation" class="shift-info-field mt-3" data-testid="shift-view-signing-explanation">
          <span>Explanation</span>
          <div class="shift-signing-explanation">{{ shift.explanation }}</div>
        </div>
      </section>

      <section>
        <section-divider title="Record Details" />
        <div class="shift-info-grid">
          <div v-if="shift.author" class="shift-info-field">
            <span>Author</span>
            <strong>{{ shift.author.first_name }} {{ shift.author.last_name }}</strong>
          </div>
          <div class="shift-info-field">
            <span>Payment Type</span>
            <strong>{{ shift.internal_payment_info.payment_type || '-' }}</strong>
          </div>
          <template v-if="getRate.incoming">
            <div class="shift-info-field">
              <span>{{ shift.external_payment_info ? 'Pay Rate' : 'Rate' }}</span>
              <strong>${{ getRate.incoming.value / 100 }}</strong>
            </div>
            <div v-if="getRate.incoming.extra" class="shift-info-field">
              <span>Extra</span>
              <strong>${{ getRate.incoming.extra / 100 }}</strong>
            </div>
          </template>
          <div v-if="hasInternalRateFlags" class="shift-info-field">
            <span>Rate Flags</span>
            <div class="flex flex-wrap gap-2">
              <a-tag v-if="shift.internal_payment_info.flags.zero_rate" class="m-0" color="var(--warning-color)">
                Zero Rate
              </a-tag>
              <a-tag v-if="shift.internal_payment_info.flags.training_period" class="m-0" color="var(--success-color)">
                Training
              </a-tag>
              <a-tag v-if="shift.internal_payment_info.flags.orientation_period" class="m-0" color="var(--info-color)">
                Orientation
              </a-tag>
            </div>
          </div>
          <div v-if="shift.version" class="shift-info-field">
            <span>Version</span>
            <strong>{{ shift.version }}</strong>
          </div>
          <div v-if="shift.version_created_at" class="shift-info-field">
            <span>Version Created At</span>
            <strong>{{ getDateTime(shift.version_created_at) }}</strong>
          </div>
          <div v-if="shift.deleted_by" class="shift-info-field">
            <span>Deleted By</span>
            <div class="shift-info-deleted-by">
              <button
                v-if="canOpenProfile(shift.deleted_by)"
                type="button"
                class="shift-info-profile-button shift-info-profile-button--photo"
                :aria-label="`Open ${associateName(shift.deleted_by) || 'associate'} profile`"
                @click="openProfile(shift.deleted_by)"
              >
                <user-avatar :width="40" :height="40" :image-path="shift.deleted_by.photo_url" :has-modal="false" />
              </button>
              <user-avatar
                v-else
                class="shift-info-deleted-by__photo"
                :width="40"
                :height="40"
                :image-path="shift.deleted_by.photo_url"
                :has-modal="false"
              />
              <div class="shift-info-deleted-by__content">
                <button
                  v-if="canOpenProfile(shift.deleted_by)"
                  type="button"
                  class="shift-info-profile-button shift-info-profile-button--name"
                  @click="openProfile(shift.deleted_by)"
                >
                  {{ associateName(shift.deleted_by) || '-' }}
                </button>
                <strong v-else>{{ associateName(shift.deleted_by) || '-' }}</strong>
                <small>Associate ID: {{ shift.deleted_by.associate_number || '-' }}</small>
                <a-tag v-if="shift.deleted_by.is_verified === false" class="m-0" color="var(--warning-color)">
                  Incomplete
                </a-tag>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <a-modal v-model="visibleNoteModal" title="Manage Note" :footer="null" :destroy-on-close="true">
      <note-view />
    </a-modal>
  </div>
</template>

<script>
import { Button, Modal, notification, Tag } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import ExportButton from '@/components/export-button.vue'
import SectionDivider from '@/components/ui/section-divider.vue'
import UserAvatar from '@/components/ui/user-avatar.vue'
import Signature from '@/components/signature.vue'
import permissionMixin from '@/mixins/permissionMixin'
import util from '@/util'
import ShiftViewInfoMain from './shift-view-info-main.vue'
import ShiftViewNotes from './shift-view-notes.vue'

const { mapState: shiftState, mapGetters: shiftGetters, mapActions: shiftActions } = createNamespacedHelpers('shifts')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-button': Button,
    'a-modal': Modal,
    'a-tag': Tag,
    'export-button': ExportButton,
    'section-divider': SectionDivider,
    'shift-main': ShiftViewInfoMain,
    'user-avatar': UserAvatar,
    signature: Signature,
    'note-view': ShiftViewNotes
  },
  mixins: [permissionMixin],
  data() {
    return {
      visibleNoteModal: false
    }
  },
  computed: {
    ...shiftState({
      shift: state => state.shift.shift,
      associate: state => state.shift.associate
    }),
    ...shiftGetters([
      'getShiftPaymentRequestStatus',
      'getShiftStatus',
      'getReadyForPaymentStatus',
      'getNotReadyForPaymentFlag',
      'getOrigin'
    ]),

    isStaff() {
      return this.$store.state.applicationState.currentProject.staffing
    },

    isActionRestricted() {
      const isBlocked =
        this.$store.state.applicationState.currentProject.staffing &&
        this.shift.internal_payment_info.project.id !== this.$store.state.applicationState.currentProject.id

      return this.shift.status === 'deleted' || isBlocked
    },

    getPaymentStatus() {
      const outgoingStatus = this.shift?.external_payment_info?.payment_status

      return {
        outgoing: outgoingStatus ? this.getShiftPaymentRequestStatus(outgoingStatus) : undefined
      }
    },

    getRate() {
      const incomingRate = this.shift.internal_payment_info
      const outgoingRate = this.shift.external_payment_info

      return {
        incoming: {
          title: incomingRate.rate_type === 'hourly' ? 'Rate' : 'Total',
          value: incomingRate.rate,
          extra: incomingRate.extra
        },
        outgoing: outgoingRate
          ? {
              title: outgoingRate.rate_type === 'hourly' ? 'Rate' : 'Total',
              value: outgoingRate.rate,
              extra: outgoingRate.extra
            }
          : undefined
      }
    },

    hasInternalRateFlags() {
      const flags = this.shift.internal_payment_info.flags

      return Boolean(flags && (flags.zero_rate || flags.training_period || flags.orientation_period))
    }
  },
  methods: {
    ...shiftActions(['sendReportShiftTimeCards', 'downloadReportShiftTimeCards']),
    ...modalActions(['openModal']),

    associateName(record) {
      return `${util.titleCase(record?.first_name)} ${util.titleCase(record?.last_name)}`.trim()
    },

    canOpenProfile(record) {
      return Boolean(record?.associate_number && this.hasPermission('manage_associates'))
    },

    openProfile(record) {
      if (!this.canOpenProfile(record)) return

      this.openModal({
        type: 'profile',
        data: {
          visible: true,
          associate_number: record.associate_number,
          edit: () => {},
          open_tab: 'personal'
        }
      })
    },

    getDateTime(date) {
      return util.convertDateTimeToCurrentTimeZoneFormattedString(date)
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    async sendReport(email) {
      const payload = {
        shift_id: this.shift.id,
        email: email.toLowerCase()
      }

      try {
        await this.sendReportShiftTimeCards(payload)

        this.showNotification('success', 'Success', 'Shift Time Card Report successfully sent to email')
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      }
    },

    async downloadReport() {
      try {
        const url = await this.downloadReportShiftTimeCards({
          shift_id: this.shift.id
        })

        util.downloadFile(url)
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      }
    }
  }
}
</script>

<style scoped>
.shift-view-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.shift-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
}

.shift-info-field {
  min-width: 0;
}

.shift-info-field > span {
  display: block;
  margin-bottom: 4px;
  color: var(--text-color-secondary);
  font-size: 12px;
}

.shift-info-field > strong {
  display: block;
  overflow-wrap: anywhere;
}

.shift-signing-explanation {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.shift-info-deleted-by {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.shift-info-deleted-by__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.shift-info-deleted-by__content > small {
  color: var(--text-color-secondary);
}

.shift-info-deleted-by__photo ::v-deep img {
  cursor: default !important;
}

.shift-info-profile-button {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.shift-info-profile-button--photo {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: var(--border-radius);
  overflow: hidden;
}

.shift-info-profile-button--name {
  color: var(--text-color);
  font-weight: 600;
  overflow-wrap: anywhere;
}

.shift-info-profile-button--name:hover {
  color: var(--primary-color);
}

.ant-tag {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
